import * as React from 'react'

import { cn } from '@/lib/utils'

function Table({ className, ...props }: React.ComponentProps<'table'>) {
    return (
        <div className="relative w-full overflow-auto">
            <table
                data-slot="table"
                className={cn('w-full caption-bottom text-sm', className)}
                {...props}
            />
        </div>
    )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
    return (
        <thead
            data-slot="table-header"
            className={cn('[&_tr]:border-b', className)}
            {...props}
        />
    )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
    return (
        <tbody
            data-slot="table-body"
            className={cn('[&_tr:last-child]:border-0', className)}
            {...props}
        />
    )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
    return (
        <tr
            data-slot="table-row"
            className={cn(
                'border-b transition-colors hover:bg-muted/50',
                className,
            )}
            {...props}
        />
    )
}

// Headers + cells default to whitespace-nowrap so labels (and short values)
// keep one line on narrow viewports — the Table wrapper already supplies
// horizontal overflow scroll. Override per cell with `whitespace-normal`
// when a column genuinely needs to wrap (e.g. long-form notes).
function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
    return (
        <th
            data-slot="table-head"
            className={cn(
                'text-muted-foreground h-10 px-3 text-left align-middle font-medium text-xs uppercase tracking-wide whitespace-nowrap',
                className,
            )}
            {...props}
        />
    )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
    return (
        <td
            data-slot="table-cell"
            className={cn('p-3 align-middle whitespace-nowrap', className)}
            {...props}
        />
    )
}

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell }
